import { Link, NavLink, Outlet } from 'react-router-dom';
import { useSession } from '@/services/auth';
import { useUIStore } from '@/stores/uiStore';
import { Icon, type IconName } from '@/components/ui/Icon/Icon';
import { nb } from '@/i18n/nb';
import type { Role } from '@/types/api';
import s from './admin.module.css';

type AdminNavItem = { to: string; label: string; icon: IconName; roles: Role[] };

// Users + access requests are owner+ (owners manage registered guests — Jon's
// 2026-06-01 decision); the audit log is admin-only. Server middleware enforces the same.
const NAV: AdminNavItem[] = [
  { to: '/admin/users', label: nb.admin.nav.users, icon: 'user', roles: ['owner', 'admin'] },
  { to: '/admin/access-requests', label: nb.admin.nav.accessRequests, icon: 'book', roles: ['owner', 'admin'] },
  { to: '/admin/innhold', label: nb.admin.nav.content, icon: 'pencil', roles: ['admin'] },
  { to: '/admin/audit-log', label: nb.admin.nav.auditLog, icon: 'list', roles: ['admin'] },
];

export function AdminShell() {
  const { user } = useSession();
  const editMode = useUIStore((state) => state.editMode);
  const toggleEditMode = useUIStore((state) => state.toggleEditMode);
  const role = user?.role;
  const items = NAV.filter((item) => (role ? item.roles.includes(role) : false));

  return (
    <section className={s.shell} aria-label={nb.admin.eyebrow}>
      <aside className={s.nav}>
        <div className={s.navBrand}>
          <span className={s.navEyebrow}>{nb.admin.eyebrow}</span>
          <span className={s.navSite}>{nb.app.name}</span>
        </div>
        <nav className={s.navList}>
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) => `${s.navItem} ${isActive ? s.navItemOn : ''}`}
            >
              <Icon name={item.icon} size={18} /> {item.label}
            </NavLink>
          ))}
        </nav>
        <div className={s.navFoot}>
          <button
            type="button"
            role="switch"
            aria-checked={editMode}
            className={s.navItem}
            onClick={toggleEditMode}
          >
            <Icon name="pencil" size={18} />{' '}
            {editMode ? nb.footer.avsluttRediger : nb.footer.rediger}
          </button>
          <Link to="/" className={s.navItem}>
            <Icon name="back" size={18} /> {nb.admin.backToPortal}
          </Link>
        </div>
      </aside>
      <div className={s.main}>
        <div className={s.mainInner}>
          <Outlet />
        </div>
      </div>
    </section>
  );
}
